import { ICacheEntry, IGetOrFetchOptions } from './IAPIQueryCache';

class ApiQueryCacheManager {
  private cache = new Map<string, ICacheEntry<unknown>>();

  public async getOrFetch<TData>(
    key: string,
    fetcher: () => Promise<TData>,
    options: IGetOrFetchOptions
  ): Promise<TData> {
    const { staleTime, cacheTime, force = false } = options;

    const now = Date.now();
    const existingEntry = this.cache.get(key) as ICacheEntry<TData> | undefined;

    /*
     * Return fresh cached data.
     */
    if (!force && existingEntry?.data !== undefined && now - existingEntry.updatedAt < staleTime) {
      existingEntry.lastAccessedAt = now;
      this.scheduleGarbageCollection(key, existingEntry, cacheTime);

      return existingEntry.data;
    }

    /*
     * Reuse an already running request.
     *
     * If multiple components request the same endpoint at the same time,
     * only one HTTP request will be sent.
     */
    if (!force && existingEntry?.promise) {
      return existingEntry.promise;
    }

    const requestPromise = fetcher();

    const nextEntry: ICacheEntry<TData> = {
      data: existingEntry?.data,
      updatedAt: existingEntry?.updatedAt ?? 0,
      lastAccessedAt: now,
      promise: requestPromise,
      cleanupTimer: existingEntry?.cleanupTimer,
    };

    this.cache.set(key, nextEntry);

    try {
      const data = await requestPromise;

      nextEntry.data = data;
      nextEntry.updatedAt = Date.now();
      nextEntry.lastAccessedAt = Date.now();
      nextEntry.promise = undefined;

      this.scheduleGarbageCollection(key, nextEntry, cacheTime);

      return data;
    } catch (error) {
      nextEntry.promise = undefined;

      /*
       * Remove failed entries when no previous successful value exists.
       */
      if (nextEntry.data === undefined) {
        this.delete(key);
      }

      throw error;
    }
  }

  public get<TData>(key: string): TData | undefined {
    const entry = this.cache.get(key) as ICacheEntry<TData> | undefined;

    if (!entry?.data) {
      return undefined;
    }

    entry.lastAccessedAt = Date.now();

    return entry.data;
  }

  public has(key: string): boolean {
    return this.cache.has(key);
  }

  public invalidate(keyOrPrefix: string): void {
    for (const key of this.cache.keys()) {
      if (key === keyOrPrefix || key.startsWith(keyOrPrefix)) {
        this.delete(key);
      }
    }
  }

  public invalidateAll(): void {
    for (const key of this.cache.keys()) {
      this.delete(key);
    }
  }

  public delete(key: string): void {
    const entry = this.cache.get(key);

    if (entry?.cleanupTimer) {
      clearTimeout(entry.cleanupTimer);
    }

    this.cache.delete(key);
  }

  private scheduleGarbageCollection<TData>(key: string, entry: ICacheEntry<TData>, cacheTime: number): void {
    if (entry.cleanupTimer) {
      clearTimeout(entry.cleanupTimer);
    }

    if (cacheTime === Infinity) {
      return;
    }

    entry.cleanupTimer = setTimeout(() => {
      const currentEntry = this.cache.get(key);

      /*
       * Do not remove an entry while its request is still running.
       */
      if (!currentEntry?.promise) {
        this.cache.delete(key);
      }
    }, cacheTime);
  }
}

const ApiQueryCache = new ApiQueryCacheManager();

export default ApiQueryCache;
